import { ExternalLink, X } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAccountSummary, usePositions } from "../../hooks/useAccountData";
import { useAdminManagedStrategies } from "../../hooks/useAdminManagedStrategies";
import { useStrategyData, type InjectedProductCatalogue } from "../../hooks/useStrategyData";
import { useStrategyHoldings } from "../../hooks/useStrategyHoldings";
import { PIE_COLORS } from "../../pages/dashboard/components/StrategyDonutCard";
import StrategyHoldingsSection, {
  buildProductColorMap,
} from "../../pages/dashboard/components/StrategyHoldingsSection";
import { getSignedWeight } from "../../services/investorStrategyService";
import type { IbkrPositionV2 } from "../../types";
import {
  formatCurrencyWithSymbol,
  formatPercentage,
  formatSignedPercentage,
} from "../../utils/numberUtils";
import {
  getRolledUpPosition,
  rollUpInvestorStrategyProducts,
  sumRolledUpWeights,
  type RolledUpProductRow,
} from "../../utils/managedStrategyRollup";
import {
  computeReconciledDrift,
  getReconciledCurrentWeight,
  getStrategyDisplayName,
  type GrossPositionCounts,
} from "../../utils/strategyUtils";

export interface AdminUserStrategyModalSummary {
  name: string | null;
  email: string | null;
  ibkrAccountId: string | null;
  tradingEnabled: boolean;
  isCustomer: boolean;
  isStrategyStale: boolean;
  dataInconsistent: boolean;
}

interface AdminUserStrategyModalProps {
  isOpen: boolean;
  userId: string | null;
  summary: AdminUserStrategyModalSummary | null;
  /**
   * Product catalogue the opening page already paged. Passed straight through
   * to useStrategyData so the modal never re-pages the same admin endpoint.
   * Omit it and the modal falls back to fetching its own copy.
   */
  products?: InjectedProductCatalogue | null;
  onClose: () => void;
}

const MODAL_ROOT_ID = "admin-strategy-modal-root";

function DriftCell({ drift }: { drift: number | null }) {
  if (drift == null) {
    return <span className="text-gray-400 dark:text-gray-500">--</span>;
  }
  const color =
    drift > 0
      ? "text-green-500"
      : drift < 0
        ? "text-red-500"
        : "text-gray-400 dark:text-gray-500";
  return <span className={color}>{formatSignedPercentage(drift)}</span>;
}

function totalPositionMktValue(positions: IbkrPositionV2[] | undefined): number {
  if (!positions) return 0;
  return positions.reduce((sum, p) => sum + (Number.isFinite(p.mktValue) ? p.mktValue : 0), 0);
}

const PositionsTable: React.FC<{
  positions: IbkrPositionV2[] | undefined;
  isLoading: boolean;
  error: string | null;
}> = ({ positions, isLoading, error }) => {
  const sorted = useMemo(() => {
    if (!positions) return [] as IbkrPositionV2[];
    return [...positions].sort((a, b) => Math.abs(b.mktValue) - Math.abs(a.mktValue));
  }, [positions]);
  const totalAbsValue = useMemo(
    () => sorted.reduce((sum, p) => sum + Math.abs(Number.isFinite(p.mktValue) ? p.mktValue : 0), 0),
    [sorted]
  );

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
        Failed to load positions: {error}
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        Loading positions…
      </div>
    );
  }
  if (sorted.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        No open positions.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-rp-surface-raised border-b border-gray-200 dark:border-rp-separator">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Contract
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Type
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Qty
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Mkt Value
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Avg Cost
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Unrealized PnL
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                % of book
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-rp-separator">
            {sorted.map(p => {
              const currency = p.currency ?? undefined;
              const pctOfBook =
                totalAbsValue > 0
                  ? (Math.abs(p.mktValue) / totalAbsValue) * 100
                  : 0;
              return (
                <tr key={`${p.conId}-${p.advisorAccountId}`} className="text-xs">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-white truncate max-w-[26rem]" title={p.description ?? String(p.conId)}>
                      {p.description ?? `conId ${p.conId}`}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                      #{p.conId}
                      {p.sector ? ` · ${p.sector}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {p.secType ?? p.assetClass ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-right text-gray-900 dark:text-white">
                    {p.position}
                  </td>
                  <td className="px-3 py-2 font-mono text-right text-gray-900 dark:text-white">
                    {formatCurrencyWithSymbol(p.mktValue, currency)}
                  </td>
                  <td className="px-3 py-2 font-mono text-right text-gray-500 dark:text-gray-400">
                    {formatCurrencyWithSymbol(p.avgCost, currency)}
                  </td>
                  <td
                    className={`px-3 py-2 font-mono text-right ${
                      p.unrealizedPnl > 0
                        ? "text-green-500"
                        : p.unrealizedPnl < 0
                          ? "text-red-500"
                          : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {formatCurrencyWithSymbol(p.unrealizedPnl, currency)}
                  </td>
                  <td className="px-3 py-2 font-mono text-right text-gray-500 dark:text-gray-400">
                    {formatPercentage(pctOfBook)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RolledUpCountCell: React.FC<{ counts: GrossPositionCounts | null; field: keyof GrossPositionCounts }> = ({
  counts,
  field,
}) => (
  <td className="px-2 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white w-10">
    {counts ? counts[field] : "--"}
  </td>
);

/**
 * The user's strategy flattened to portfolio/basket level: direct holdings plus
 * every managed strategy's children, merged one row per product.
 */
const UnderlyingProductsTable: React.FC<{
  rows: RolledUpProductRow[];
  isLoading: boolean;
}> = ({ rows, isLoading }) => {
  const totals = useMemo(() => sumRolledUpWeights(rows), [rows]);

  return (
    <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-rp-separator">
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3 w-16">
                ID
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left px-4 py-3">
                Product
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left px-4 py-3 w-20">
                Position
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                Long
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                Short
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                Opt
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                Allocated
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                Current
              </th>
              <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                Drift
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                  Resolving managed strategies…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                  No underlying products could be resolved.
                </td>
              </tr>
            ) : (
              <>
                {rows.map(row => {
                  const drift =
                    row.currentWeight === null ? null : row.currentWeight - row.allocatedWeight;
                  return (
                    <tr
                      key={`${row.entityType}-${row.productId}`}
                      className="border-b border-gray-100 dark:border-rp-separator hover:bg-gray-100 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
                        {row.productId}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="text-sm font-medium text-gray-900 dark:text-white truncate"
                          title={row.displayName}
                        >
                          {row.displayName}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        {getRolledUpPosition(row)}
                      </td>
                      <RolledUpCountCell counts={row.counts} field="long" />
                      <RolledUpCountCell counts={row.counts} field="short" />
                      <RolledUpCountCell counts={row.counts} field="options" />
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                        {formatSignedPercentage(row.allocatedWeight)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                        {row.currentWeight === null
                          ? "--"
                          : formatSignedPercentage(row.currentWeight)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm">
                        <DriftCell drift={drift} />
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 dark:bg-rp-surface-raised font-semibold">
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Total
                  </td>
                  <td className="px-4 py-2.5" />
                  <td className="px-2 py-2.5" />
                  <td className="px-2 py-2.5" />
                  <td className="px-2 py-2.5" />
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                    {formatSignedPercentage(totals.allocated)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                    {totals.current === null ? "--" : formatSignedPercentage(totals.current)}
                  </td>
                  <td className="px-4 py-2.5" />
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ModalBody: React.FC<{
  userId: string;
  summary: AdminUserStrategyModalSummary | null;
  products: InjectedProductCatalogue | null;
  onClose: () => void;
}> = ({ userId, summary, products, onClose }) => {
  // This modal renders composition, holdings and positions only -- no update
  // history and no performance chart -- so it opts out of both of those fetches
  // and reuses the catalogue the opening page already loaded.
  const strategyDataOptions = useMemo(
    () => ({ fetchHistory: false, fetchFactors: false, products }),
    [products]
  );
  const strategyData = useStrategyData(userId, strategyDataOptions);
  const {
    strategies,
    reservedEntries,
    productLookup,
    productItems,
    basketLookup,
    positionCountsMap,
    isLoading: isLoadingStrategy,
    strategyError,
  } = strategyData;

  // A managed strategy is a held product like any other, so the composition
  // table shows every record. Sorted by [entityType, productId] to match the
  // order the backend documents for the bulk endpoint.
  const records = useMemo(() => {
    const all = [...strategies, ...reservedEntries];
    return all.sort((a, b) =>
      a.entityType === b.entityType
        ? a.productId - b.productId
        : a.entityType.localeCompare(b.entityType)
    );
  }, [strategies, reservedEntries]);

  const managedIds = useMemo(
    () =>
      records
        .filter(record => record.entityType === "MANAGED_STRATEGY")
        .map(record => record.productId),
    [records]
  );
  const hasManagedStrategy = managedIds.length > 0;
  const managed = useAdminManagedStrategies(managedIds, true);

  const rollup = useMemo(
    () =>
      rollUpInvestorStrategyProducts(
        records,
        managed.managedById,
        productItems,
        basketLookup
      ),
    [records, managed.managedById, productItems, basketLookup]
  );

  // A short rolled-up view on a live book reads as "the user holds less than
  // they do", so an incomplete expansion must always be visible.
  const rollupWarning = useMemo(() => {
    if (managed.isLoading) return null;
    // Count distinct strategies, not issue rows: a failed fetch also surfaces
    // as MANAGED_NOT_FOUND from the rollup, and must not be counted twice.
    const unresolvedManaged = managedIds.filter(id => !managed.managedById.has(id)).length;
    if (unresolvedManaged > 0) {
      return `Rolled-up view is incomplete: ${unresolvedManaged} managed strateg${
        unresolvedManaged === 1 ? "y" : "ies"
      } could not be loaded.`;
    }
    if (rollup.issues.some(issue => issue.code === "MANAGED_COMPOSITION_INVALID")) {
      return "Rolled-up view is incomplete: a managed strategy composition is unavailable.";
    }
    if (rollup.issues.some(issue => issue.code === "PRODUCT_NOT_FOUND")) {
      return "Rolled-up view is incomplete: a product could not be resolved from the catalogue.";
    }
    return null;
  }, [managed.isLoading, managed.managedById, managedIds, rollup.issues]);

  const accountSummary = useAccountSummary(userId);
  const positionsQuery = usePositions(userId);

  // Holdings roll up from the flattened product set, so a managed strategy's
  // leaves are included. Allocated/current map onto the weight/driftedWeight
  // pair the existing leaf math already resolves.
  const holdingsInput = useMemo(
    () =>
      rollup.rows.map(row => ({
        entityType: row.entityType,
        productId: row.productId,
        weight: row.allocatedWeight,
        driftedWeight: row.currentWeight,
        displayName: row.displayName,
      })),
    [rollup.rows]
  );
  const holdings = useStrategyHoldings(holdingsInput, productItems, basketLookup);
  const productColorMap = useMemo(() => buildProductColorMap(records), [records]);

  const nlv = accountSummary.data?.netLiquidationSecurity ?? null;
  const totalMktValue = totalPositionMktValue(positionsQuery.data);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} aria-hidden="true" />
      <div className="relative flex min-h-full items-start justify-center p-4 sm:p-6">
        <div className="relative w-full max-w-6xl bg-white dark:bg-black border border-gray-200 dark:border-rp-separator rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 dark:border-rp-separator bg-white dark:bg-black">
            <div className="min-w-0">
              {(summary?.name || summary?.email) && (
                <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                  {summary?.name && (
                    <span className="text-base font-semibold text-gray-900 dark:text-white truncate max-w-[28rem]">
                      {summary.name}
                    </span>
                  )}
                  {summary?.email && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[28rem]">
                      {summary.email}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Strategy
                </h2>
                <span className="font-mono text-sm text-gray-900 dark:text-white break-all">
                  {userId}
                </span>
                {summary?.ibkrAccountId && (
                  <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-300">
                    {summary.ibkrAccountId}
                  </span>
                )}
                {summary?.tradingEnabled && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rp-amber/10 text-rp-amber border border-rp-amber/30">
                    Trading
                  </span>
                )}
                {summary?.isCustomer && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rp-amber/10 text-rp-amber border border-rp-amber/30">
                    Customer
                  </span>
                )}
                {summary?.dataInconsistent && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                    Inconsistent
                  </span>
                )}
                {!summary?.dataInconsistent && summary?.isStrategyStale && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                    Drift unavailable
                  </span>
                )}
              </div>
              {nlv !== null && (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                  NLV {formatCurrencyWithSymbol(nlv)}
                  {totalMktValue > 0 && (
                    <>
                      {" · "}Σ positions {formatCurrencyWithSymbol(totalMktValue)}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/admin/users/${userId}/strategy`}
                className="inline-flex items-center gap-1 text-xs font-medium text-rp-amber hover:underline"
                title="Open full strategy page"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Full page
              </Link>
              <button
                onClick={onClose}
                className="rounded-md p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-rp-surface-raised"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6 bg-gray-50 dark:bg-black">
            {strategyError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
                {strategyError}
              </div>
            )}

            {products?.error && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
                {products.error}
              </div>
            )}

            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Strategy Composition
              </h3>
              <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-rp-separator">
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3 w-16">
                          ID
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left px-4 py-3">
                          Product
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left px-4 py-3 w-20">
                          Position
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                          Long
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                          Short
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-2 py-3 w-10">
                          Opt
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                          Allocated
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                          Current
                        </th>
                        <th className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right px-4 py-3">
                          Drift
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingStrategy && records.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                            Loading strategy…
                          </td>
                        </tr>
                      ) : records.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                            No strategy configured for this investor.
                          </td>
                        </tr>
                      ) : (
                        records.map((entry, idx) => {
                          const isManaged = entry.entityType === "MANAGED_STRATEGY";
                          const name = isManaged
                            ? managed.managedById.get(entry.productId)?.title
                              ?? `MANAGED_STRATEGY #${entry.productId}`
                            : getStrategyDisplayName(entry, productLookup);
                          const color = PIE_COLORS[idx % PIE_COLORS.length];
                          const counts = isManaged
                            ? rollup.managedCounts.get(entry.productId) ?? undefined
                            : positionCountsMap.get(`${entry.entityType}-${entry.productId}`);
                          const signedWeight = getSignedWeight(entry);
                          // Sign is the source of truth. entry.position is a text
                          // declaration that can disagree with the weight the math
                          // uses, and current can arrive unsigned on a short.
                          const signedCurrent = getReconciledCurrentWeight(entry);
                          const drift = computeReconciledDrift(entry);
                          return (
                            <tr
                              key={`${entry.entityType}-${entry.productId}`}
                              className="border-b border-gray-100 dark:border-rp-separator last:border-b-0 hover:bg-gray-100 dark:hover:bg-white/[0.02] transition-colors"
                            >
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
                                {entry.productId}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={name}>
                                    {name}
                                  </span>
                                  {isManaged && (
                                    <span className="flex-shrink-0 px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                                      Managed
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                {signedWeight < 0 ? "SHORT" : "LONG"}
                              </td>
                              <td className="px-2 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white w-10">
                                {counts ? counts.long : "--"}
                              </td>
                              <td className="px-2 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white w-10">
                                {counts ? counts.short : "--"}
                              </td>
                              <td className="px-2 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white w-10">
                                {counts ? counts.options : "--"}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                                {formatSignedPercentage(signedWeight)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                                {signedCurrent == null ? "--" : formatSignedPercentage(signedCurrent)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums text-sm">
                                <DriftCell drift={drift} />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {hasManagedStrategy && (
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Underlying Products (rolled-up)
                </h3>
                {rollupWarning && (
                  <div className="mb-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
                    {rollupWarning}
                  </div>
                )}
                <UnderlyingProductsTable
                  rows={rollup.rows}
                  isLoading={managed.isLoading || isLoadingStrategy}
                />
              </section>
            )}

            {records.length > 0 && (
              <section>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Underlying Holdings (rolled-up)
                </h3>
                {hasManagedStrategy && rollupWarning && (
                  <div className="mb-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
                    {rollupWarning}
                  </div>
                )}
                <StrategyHoldingsSection
                  holdings={holdings.holdings}
                  summary={holdings.summary}
                  missingProducts={holdings.missingProducts}
                  isLoading={holdings.isLoading || isLoadingStrategy || managed.isLoading}
                  nlv={nlv}
                  title="Underlying Holdings"
                  productColorMap={productColorMap}
                />
              </section>
            )}

            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Positions (IBKR)
              </h3>
              <PositionsTable
                positions={positionsQuery.data}
                isLoading={positionsQuery.isLoading}
                error={positionsQuery.error ? positionsQuery.error.message : null}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

function ensureModalRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let root = document.getElementById(MODAL_ROOT_ID);
  if (!root) {
    root = document.createElement("div");
    root.id = MODAL_ROOT_ID;
    document.body.appendChild(root);
  }
  return root;
}

const AdminUserStrategyModal: React.FC<AdminUserStrategyModalProps> = ({
  isOpen,
  userId,
  summary,
  products,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !userId) return null;
  const root = ensureModalRoot();
  if (!root) return null;

  return createPortal(
    <ModalBody
      userId={userId}
      summary={summary}
      products={products ?? null}
      onClose={onClose}
    />,
    root
  );
};

export default AdminUserStrategyModal;
